import { useEffect, useState } from "react";
import axios from '../axiosInstance';
import jwt_decode from "jwt-decode";

function Toast({ message, type = "error", onClose }) {
  return (
    <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded shadow-lg text-white ${type === "error" ? "bg-red-600" : "bg-green-600"}`}>
      <div className="flex items-center space-x-2">
        <span>{message}</span>
        <button onClick={onClose} className="ml-2 text-white hover:text-gray-300 font-bold">×</button>
      </div>
    </div>
  );
}

function TradeModal({ order, quantity, setQuantity, funds, onConfirm, onClose }) {
  if (!order) return null;

  const totalPrice = order.price * (parseFloat(quantity) || 0);
  const isBuying = order.type === "sell";
  const hasEnoughFunds = isBuying
    ? funds.coinz >= totalPrice
    : funds.options >= (parseFloat(quantity) || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800/90 rounded-lg shadow-lg p-6 w-full max-w-md space-y-4">
        <h2 className="text-xl font-bold text-white-800">
          {isBuying ? "Buy options" : "Sell options"}
        </h2>

        <p className="text-sm text-grey-900">
          Prix unitaire : <strong>{Number(order.price).toFixed(5)} coinz</strong>
        </p>

        <div className="space-y-2">
          <label className="block text-grey-700 text-sm">Quantity</label>
          <input
            type="number"
            
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="bg-gray-800 w-full border px-4 py-2 rounded"
            min="1"
            step="1"
          />
        </div>

		<p className="text-white-700 text-sm">
		  Total to {isBuying ? "Pay" : "Receive"}: <strong>{totalPrice.toFixed(5)} coinz</strong>
		</p>

		{!isBuying && (
		  <p className="text-xs text-red-400 italic">
			A 5% commission will be deducted from your proceeds.
		  </p>
)}

        <p className={`text-sm ${hasEnoughFunds ? "text-green-600" : "text-red-600"}`}>
          {isBuying
            ? ` Coinz available : ${(parseFloat(funds.coinz) || 0).toFixed(2)}`
            : ` Options available : ${(parseFloat(funds.options) || 0).toFixed(2)}`}
        </p>

        <div className="flex justify-end space-x-4 pt-4">
          <button
            onClick={onClose}
            className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(order.id, quantity)}
            className={`px-4 py-2 rounded text-white ${
              hasEnoughFunds ? "bg-green-600 hover:bg-green-700" : "bg-gray-400 cursor-not-allowed"
            }`}
            disabled={!hasEnoughFunds}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}


export default function OrderBookSection() {
  const [buyOrders, setBuyOrders] = useState([]);
  const [sellOrders, setSellOrders] = useState([]);
  const [orderType, setOrderType] = useState("buy");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [funds, setFunds] = useState({ coinz: 0, options: 0 });
  const [toast, setToast] = useState({ message: "", type: "" });
  const [userOrders, setUserOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalQuantity, setModalQuantity] = useState("");
  const [buyPage, setBuyPage] = useState(0);
  const [sellPage, setSellPage] = useState(0);
  const [userId, setUserId] = useState(0);
  const ORDERS_PER_PAGE = 10;
 

  const sortOrders = (orders, type) => {
    return orders
      .sort((a, b) => {
        if (type === "buy") {
          return b.price - a.price;
        } else {
          return a.price - b.price;
        }
      })
      .sort((a, b) => b.timestamp - a.timestamp);
  };

  const fetchUserOrders = async () => {
 
    try {
      const res = await axios.get(`/api/myorders`, { withCredentials: true });
      const allOrders = res.data;
      if (!allOrders.message) {
        setUserOrders(allOrders);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des ordres :", err);
    }
  };

  const showToast = (message, type = "error") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };
  
  const fetchFunds = async () => {
   
    try {
      const res = await axios.get(`/api/funds/user`, { withCredentials: true });
      setFunds(res.data);
    } catch (err) {
      console.error("Erreur lors du chargement des fonds :", err);
    }
  };
 
  const getUserId = async () => {
	axios.get(`/api/user/me`, { withCredentials: true }) 
      .then((response) => {
     setUserId(response.data.user_Id);
      
      })
      .catch((error) => {
		  console.log(error);      
      });  
	  
  };

  const fetchOrders = async () => {
    
    try {
      const res = await axios.get(`/api/orders`, { withCredentials: true });
      const allOrders = res.data;
      if (!allOrders.message) {
        const buyOrders = sortOrders(allOrders.filter((o) => o.type === "buy"), "buy");
        const sellOrders = sortOrders(allOrders.filter((o) => o.type === "sell"), "sell");

        setBuyOrders(buyOrders);
        setSellOrders(sellOrders);

        return { buyOrders, sellOrders };
      }

      return { buyOrders: [], sellOrders: [] };
    } catch (err) {
      console.error("Erreur lors de la récupération des ordres :", err);
      return { buyOrders: [], sellOrders: [] };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!Number.isInteger(Number(quantity))) {
      showToast("Quantity must be an integer");
      return;
    }

	const newOrder = {
      type: orderType,
      price: parseFloat(price),
      amount: parseFloat(quantity),
    };

	
    try {
      const response = await axios.post(`/api/orders`,newOrder,
        { withCredentials: true }
      );

      if (response.data.success) {
        // await matchOrders(nouvelOrdre);
        fetchOrders();
        fetchFunds();
        fetchUserOrders();
        showToast("Order successfully placed", "success");
      }

      setQuantity("");
      setPrice("");
    } catch (err) {
      showToast(err.response?.data?.error || "Order cancellation error", "error");
	  console.log("erreur : ", err);
    }
  };

  const handleQuickTrade = async (orderId, quantity) => {
    if (!Number.isInteger(Number(quantity))) {
      showToast("Quantity must be an integer");
      return;
    }
    try {
      await axios.post(
        `/api/orders/execute/${orderId}`,
        { quantity: parseFloat(quantity) },
        { withCredentials: true }
      );
      fetchOrders();
      fetchFunds();
      showToast("Transaction effectuée avec succès", "success");
      setModalVisible(false);
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur inconnue", "error");
    }
  };
 


  const handleCancelOrder = async (orderId, amount, price) => {
   
    try {
      await axios.post(`/api/orders/cancel/${orderId}`,{ withCredentials: true });
      fetchUserOrders();
      fetchOrders();
      fetchFunds();
      showToast("Ordre annulé avec succès", "success");
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors de l'annulation de l'ordre", "error");
	  console.log("erreur : ", err);
    }
  };

  useEffect(() => {
    getUserId(); // une seule fois au montage
    fetchUserOrders();
    fetchOrders();
    fetchFunds();
  }, []);
  
  useEffect(() => {
    const refreshOrders = () => {
      fetchOrders(); // seule fonction qu'on met à jour périodiquement
    };
  
    refreshOrders(); // appel initial
    const interval = setInterval(refreshOrders, 10000); // toutes les 10s
  
    return () => clearInterval(interval); // nettoyage
  }, []);


return (
  <>
    {toast.message && (
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "" })}
      />
    )}

    {modalVisible && (
      <TradeModal
        order={selectedOrder}
        quantity={modalQuantity}
        setQuantity={setModalQuantity}
        funds={funds}
        onConfirm={handleQuickTrade}
        onClose={() => setModalVisible(false)}
      />
    )}

<div className="space-y-6 bg-gray-800/50 p-6 rounded-lg shadow-md">
  <h2 className="text-xl font-bold text-white">Order Book</h2>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {/* Buy Orders */}
    <div className="bg-gray-900 p-4 rounded-lg shadow-lg overflow-x-auto">
      <h3 className="text-lg font-semibold text-gray-300 mb-2">Buy Orders</h3>
      <table className="min-w-full text-sm text-left text-gray-300">
        <thead className="text-xs uppercase text-gray-500 border-b">
          <tr>
            <th className="py-2 px-2">Quantity</th>
            <th className="py-2 px-2">Price (coinz)</th>
            <th className="py-2 px-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {buyOrders
            .slice(buyPage * ORDERS_PER_PAGE, (buyPage + 1) * ORDERS_PER_PAGE)
            .map((order, index) => (
              <tr key={index} className="border-b hover:bg-gray-700">
                <td className="py-2 px-2">{order.amount}</td>
                <td className="py-2 px-2">{Number(order.price).toFixed(5)}</td>
                <td className="py-2 px-2">
                  {buyOrders[0]?.id === order.id &&
                    Number(order.userId) !== Number(userId) && (
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setModalQuantity(order.amount);
                          setModalVisible(true);
                        }}
                        className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-xs"
                      >
                        Sell your options
                      </button>
                    )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <div className="flex justify-between items-center mt-2 text-xs">
        <button
          onClick={() => setBuyPage((prev) => Math.max(prev - 1, 0))}
          disabled={buyPage === 0}
          className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
        >
          Previous
        </button>
        <button
          onClick={() =>
            setBuyPage((prev) =>
              (prev + 1) * ORDERS_PER_PAGE < buyOrders.length ? prev + 1 : prev
            )
          }
          disabled={(buyPage + 1) * ORDERS_PER_PAGE >= buyOrders.length}
          className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>

    {/* Sell Orders */}
    <div className="bg-gray-900 p-4 rounded-lg shadow-lg overflow-x-auto">
      <h3 className="text-lg font-semibold text-gray-300 mb-2">Sell Orders</h3>
      <table className="min-w-full text-sm text-left text-gray-300">
        <thead className="text-xs uppercase text-gray-500 border-b">
          <tr>
            <th className="py-2 px-2">Quantity</th>
            <th className="py-2 px-2">Price (coinz)</th>
            <th className="py-2 px-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {sellOrders
            .slice(sellPage * ORDERS_PER_PAGE, (sellPage + 1) * ORDERS_PER_PAGE)
            .map((order, index) => (
              <tr key={index} className="border-b hover:bg-gray-700">
                <td className="py-2 px-2">{order.amount}</td>
                <td className="py-2 px-2">{Number(order.price).toFixed(5)}</td>
                <td className="py-2 px-2">
                  {sellOrders[0]?.id === order.id &&
                    Number(order.userId) !== Number(userId) && (
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setModalQuantity(order.amount);
                          setModalVisible(true);
                        }}
                        className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-xs"
                      >
                        Buy options
                      </button>
                    )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <div className="flex justify-between items-center mt-2 text-xs">
        <button
          onClick={() => setSellPage((prev) => Math.max(prev - 1, 0))}
          disabled={sellPage === 0}
          className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
        >
          Previous
        </button>
        <button
          onClick={() =>
            setSellPage((prev) =>
              (prev + 1) * ORDERS_PER_PAGE < sellOrders.length ? prev + 1 : prev
            )
          }
          disabled={(sellPage + 1) * ORDERS_PER_PAGE >= sellOrders.length}
          className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  </div>

  {/* Formulaire responsive */}
  <div className="bg-gray-900 p-4 rounded-lg shadow-lg mt-6">
    <h3 className="text-lg font-semibold text-white mb-4">Place an Order</h3>
    <p className="text-sm text-gray-400 mb-4">
      Available funds:
      <span className="ml-2 text-white">{(parseFloat(funds.coinz) || 0).toFixed(2)} coinz</span> |
      <span className="ml-2 text-white">{(parseFloat(funds.options) || 0).toFixed(2)} options</span>
    </p>
    <form
      onSubmit={handleSubmit}
      className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0"
    >
      <select
        value={orderType}
        onChange={(e) => setOrderType(e.target.value)}
        className="border bg-gray-900 px-4 py-2 rounded-lg w-full sm:w-[120px]"
      >
        <option value="buy">Buy</option>
        <option value="sell">Sell</option>
      </select>

      <input
        type="number"
        placeholder="Quantity"
        className="border bg-gray-900 px-4 py-2 rounded-lg w-full sm:w-[120px]"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        step="1"
        min="1"
      />

      <input
        type="number"
        placeholder="Price"
        className="border bg-gray-900 px-4 py-2 rounded-lg w-full sm:w-[120px]"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
      />

      <button
        type="submit"
        className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition duration-300 ease-in-out transform hover:scale-105 w-full sm:w-auto"
      >
        Submit
      </button>
    </form>
    {orderType === "sell" && quantity && price && (
      <div className="mt-4 text-sm text-gray-300">
        <p>
          Order value:{" "}
          <span className="text-white">{(quantity * price).toFixed(2)} coinz</span>
        </p>
        <p>
          Immediate commission (5%):{" "}
          <span className="text-red-400">-{(quantity * price * 0.05).toFixed(2)} coinz</span>
        </p>
        <p className="italic text-gray-400">
          This fee will be deducted from your coinz balance immediately when placing this order, whether or not the order is executed.
        </p>
      </div>
    )}
  </div>

  {/* My Orders */}
  <div className="bg-gray-900 p-4 rounded-lg shadow-lg mt-6 overflow-x-auto">
    <h3 className="text-lg font-semibold text-white mb-4">My Orders</h3>
    <table className="min-w-full text-sm text-left text-gray-300">
      <thead className="text-xs uppercase text-gray-500 border-b">
        <tr>
          <th className="py-2 px-2">Type</th>
          <th className="py-2 px-2">Quantity</th>
          <th className="py-2 px-2">Price</th>
          <th className="py-2 px-2">Action</th>
        </tr>
      </thead>
      <tbody>
        {userOrders.map((order, index) => (
          <tr key={index} className="border-b hover:bg-gray-700">
            <td className="py-2 px-2">{order.type === "buy" ? "Buy" : "Sell"}</td>
            <td className="py-2 px-2">{order.amount}</td>
            <td className="py-2 px-2">{Number(order.price).toFixed(5)}</td>
            <td className="py-2 px-2">
              <button
                onClick={() => handleCancelOrder(order.id, order.amount, order.price, showToast)}
                className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-xs"
              >
                Cancel
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>
  </>
);

}